import * as React from 'react';

import { cn } from './lib/cn';

export type TabsSize = 'sm' | 'md';

export interface Tab<K extends string> {
  key: K;
  label: string;
  count?: number | undefined;
}

export interface TabsProps<K extends string> {
  tabs: ReadonlyArray<Tab<K>>;
  active: K;
  onChange: (key: K) => void;
  /** Namespaces the tab and panel ids. Pass the same value to the paired `TabPanel`. */
  idBase: string;
  size?: TabsSize;
  className?: string;
  /**
   * Keep the bar on one line and scroll it sideways inside itself when the tabs
   * are wider than the bar, instead of wrapping (IMM-1315). For a page-level tab
   * bar whose tab set and counts grow with the record. Off by default: a scroll
   * container's automatic minimum width is 0, so inside a toolbar row it would be
   * squeezed rather than kept (BL-062).
   */
  scrollable?: boolean;
}

export const tabId = (idBase: string, key: string): string => `${idBase}-tab-${key}`;
export const tabPanelId = (idBase: string, key: string): string => `${idBase}-panel-${key}`;

// A wrapping bar only has a minimum height and each tab carries the row height: at phone width the tabs wrap, and a
// fixed bar height let the second row spill over the content below it. A scrollable bar stays one row, so it is fixed.
const BAR_HEIGHTS: Record<TabsSize, string> = { sm: 'min-h-8', md: 'min-h-10' };
const TAB_HEIGHTS: Record<TabsSize, string> = { sm: 'h-8', md: 'h-10' };

/**
 * Scroll the list — never the page — so the selected tab's whole box, plus the
 * list's inline padding, is in view. `scrollIntoView` would also scroll `<main>`
 * (IMM-1315 D5). Untouched when the tab is already fully visible; in jsdom every
 * measure is 0, so this is a no-op there.
 */
function revealSelectedTab(list: HTMLElement, tab: HTMLElement): void {
  const style = getComputedStyle(list);
  const start = tab.offsetLeft - (parseFloat(style.paddingLeft) || 0);
  const end = tab.offsetLeft + tab.offsetWidth + (parseFloat(style.paddingRight) || 0) - list.clientWidth;

  if (start < list.scrollLeft) list.scrollLeft = start;
  else if (end > list.scrollLeft) list.scrollLeft = end;
}

/**
 * The active tab is signalled by underline **and** weight, never by colour
 * alone. That pairing is Phase B Anpassung #3 and the reason
 * `--font-weight-tab` / `-active` exist; both were referenced zero times in the
 * monorepo before this component, because they had no Tailwind mapping to be
 * referenced through.
 *
 * `--border-tab-active` is a full `border-bottom` shorthand, which no Tailwind
 * utility can consume, so the selected tab reads it through the arbitrary
 * property `[border-bottom:var(--border-tab-active)]` rather than reproducing
 * its value.
 *
 * Activation follows focus: every call site swaps an in-page panel against
 * local state, so a selection costs a re-render and never a navigation. A call
 * site that routes on change would need manual activation instead, or every
 * arrow key would push a history entry.
 */
export function Tabs<K extends string>({
  tabs,
  active,
  onChange,
  idBase,
  size = 'md',
  className,
  scrollable = false,
}: TabsProps<K>) {
  const refs = React.useRef(new Map<K, HTMLButtonElement>());
  const listRef = React.useRef<HTMLDivElement>(null);
  const tabsSignature = tabs.map((tab) => `${tab.key}:${tab.label}:${tab.count ?? ''}`).join('|');

  React.useLayoutEffect(() => {
    if (!scrollable) return;
    const list = listRef.current;
    const tab = refs.current.get(active);
    if (list && tab) revealSelectedTab(list, tab);
  }, [active, scrollable, tabsSignature]);

  const move = (from: K, delta: number) => {
    const index = tabs.findIndex((tab) => tab.key === from);
    const next = tabs[(index + delta + tabs.length) % tabs.length];
    if (!next) return;
    onChange(next.key);
    refs.current.get(next.key)?.focus();
  };

  const onKeyDown = (event: React.KeyboardEvent, key: K) => {
    const jump: Record<string, () => void> = {
      ArrowRight: () => move(key, 1),
      ArrowLeft: () => move(key, -1),
      Home: () => tabs[0] && (onChange(tabs[0].key), refs.current.get(tabs[0].key)?.focus()),
      End: () => {
        const last = tabs[tabs.length - 1];
        if (last) {
          onChange(last.key);
          refs.current.get(last.key)?.focus();
        }
      },
    };
    const handler = jump[event.key];
    if (!handler) return;
    event.preventDefault();
    handler();
  };

  return (
    <div
      ref={listRef}
      role="tablist"
      className={cn(
        scrollable
          ? 'relative flex items-end gap-x-5 overflow-x-auto overflow-y-hidden [scrollbar-width:thin] shadow-[inset_0_-1px_0_var(--color-neutral-100)]'
          : 'flex flex-wrap items-end gap-x-5 gap-y-2 border-b border-neutral-100',
        scrollable ? TAB_HEIGHTS[size] : BAR_HEIGHTS[size],
        className,
      )}
    >
      {tabs.map((tab) => {
        const selected = tab.key === active;

        return (
          <button
            key={tab.key}
            ref={(node) => {
              if (node) refs.current.set(tab.key, node);
              else refs.current.delete(tab.key);
            }}
            type="button"
            role="tab"
            id={tabId(idBase, tab.key)}
            aria-selected={selected}
            {...(selected ? { 'aria-controls': tabPanelId(idBase, tab.key) } : {})}
            tabIndex={selected ? 0 : -1}
            onClick={() => onChange(tab.key)}
            onKeyDown={(event) => onKeyDown(event, tab.key)}
            className={cn(
              'relative inline-flex items-center gap-2 text-base transition-[color] duration-micro ease-out',
              scrollable
                ? 'shrink-0 whitespace-nowrap focus-visible:[outline-style:solid] focus-visible:outline-4 focus-visible:-outline-offset-4 focus-visible:outline-focus-inset'
                : '-mb-px focus-visible:outline-none focus-visible:shadow-focus',
              TAB_HEIGHTS[size],
              selected
                ? '[border-bottom:var(--border-tab-active)] font-tab-active text-text-primary'
                : 'border-b-2 border-transparent font-tab text-text-tertiary hover:text-text-secondary',
            )}
          >
            {tab.label}
            {tab.count !== undefined && (
              <span className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-md bg-neutral-100 px-1.5 text-xs text-text-tertiary">
                {tab.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

export interface TabPanelProps<K extends string> {
  idBase: string;
  tabKey: K;
  className?: string;
  children: React.ReactNode;
}

export function TabPanel<K extends string>({ idBase, tabKey, className, children }: TabPanelProps<K>) {
  return (
    <div role="tabpanel" id={tabPanelId(idBase, tabKey)} aria-labelledby={tabId(idBase, tabKey)} className={className}>
      {children}
    </div>
  );
}
